import React, {useState} from 'react';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {RenameListItem} from 'app/components/general/RenameListItem';
import {DashboardLoadingContent} from 'app/documents/components/DocumentsDashboard/DashboardLoadingContent';
import {ItemWithChildrenStructure} from 'app/general/structures/ItemWithChildrenStructure';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {
  useDeleteRole,
  useRemoveUserFromRole,
  useUpdateRole,
} from 'app/roles/api';
import {AddRoleMemberPopover} from 'app/roles/components/AddMemberToRoleButton';
import {useUsers} from 'app/user/api';
import {gt} from 'app/utils/i18n';

import {TeamRolesUserListItem} from './TeamRolesUserListItem';

import type {
  actionsGetterType,
  MenuItemsType,
} from 'app/general/widgets/ThreeDotsDropdownMenu';
import type {
  RoleSerializedType,
  TeamSerializedType,
  UserSerializedType,
} from 'sweet-types';

interface Props {
  object: RoleSerializedType;
  team: TeamSerializedType;
  memberActionsGetter: actionsGetterType<UserSerializedType>;
}

export function TeamRolesListItem({
  object: role,
  team,
  memberActionsGetter,
}: Props) {
  // RTK API hooks
  const [updateRole] = useUpdateRole();
  const [deleteRole] = useDeleteRole();

  const {data: users, isLoading} = useUsers({id: role.users});

  const [removeUserFromRole] = useRemoveUserFromRole();

  const [renaming, setRenaming] = useState(false);

  const [popoverTarget, setPopoverTarget] = useState<null | HTMLElement>(null);
  const actionButtonRef = React.useRef<HTMLDivElement | null>(null);

  return (
    <ItemWithChildrenStructure
      containerClass="mb-3"
      childrenClass="border-bottom"
      actionsChildren={
        team.can_edit ? (
          <ActionsButton
            ref={actionButtonRef}
            actions={[
              {
                text: gt('Add member to role'),
                icon: 'fas fa-user-plus',
                key: 'remove-add-member',
                callback: () => setPopoverTarget(actionButtonRef.current),
              },
              {
                text: gt('Rename'),
                icon: 'fa fa-pen',
                key: 'rename-role',
                callback: () => setRenaming(true),
              },
              {
                text: gt('Remove from team'),
                icon: 'fa fa-times',
                key: 'remove-role',
                callback: () => deleteRole({role}),
              },
            ]}
          />
        ) : null
      }
      expandedChildren={
        <>
          {isLoading ? (
            <DashboardLoadingContent />
          ) : users != null && users.results.length > 0 ? (
            users.results.map((user) => (
              <TeamRolesUserListItem
                key={user.url}
                object={user}
                team={team}
                actionsGetter={(user, buttonRef) =>
                  (
                    [
                      {
                        text: gt('Remove from Role'),
                        key: 'remove-from-role',
                        icon: 'fa fa-times',
                        condition: () => team.can_edit,
                        callback: () => {
                          removeUserFromRole({role, user});
                        },
                      },
                    ] as MenuItemsType
                  ).concat(memberActionsGetter(user, buttonRef))
                }
              />
            ))
          ) : (
            <div className="py-2 font-italic border-bottom text-center text-secondary">
              <p>{gt("This role doesn't have members yet")}.</p>
              <div className="mb-2">
                <LazyTooltip
                  hide={team.can_edit}
                  tooltip={gt('You must be a manager')}
                >
                  <div>
                    <button
                      disabled={!team.can_edit}
                      className="btn btn-outline-primary btn-sm"
                      onClick={(e) => setPopoverTarget(e.currentTarget)}
                    >
                      {gt('Add members to this role')}
                    </button>
                  </div>
                </LazyTooltip>
              </div>
            </div>
          )}
        </>
      }
    >
      <div className="py-2 d-flex align-items-center flex-grow-1">
        <i className="fas fa-fw fa-user-tag mr-2 ml-2" />
        {renaming ? (
          <RenameListItem
            initialValue={role.name}
            onSave={(name) =>
              updateRole({roleURL: role.url, updates: {name}}).then(() =>
                setRenaming(false)
              )
            }
            onCancel={() => setRenaming(false)}
          />
        ) : (
          <span className="text-dark font-weight-bold">{role.name}</span>
        )}
        {popoverTarget != null ? (
          <AddRoleMemberPopover
            overlayProps={{
              placement: 'auto',
              isOpen: popoverTarget != null,
              target: popoverTarget,
              toggle: () => setPopoverTarget(null),
            }}
            role={role}
          />
        ) : null}
      </div>
    </ItemWithChildrenStructure>
  );
}
